import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Descricao from '@/components/tipografia/Descricao';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiRefresh } from '@mdi/js';
import type {
    RadiusAuthTentativaDTO,
    RadiusAuthTentativaListDTO,
    RadiusAuthResultadoDTO,
} from '@webip/shared/src/dto/radius_auth';

/**
 * Tentativas de autenticação RADIUS das últimas 24 horas.
 *
 * A linha mais informativa é a `incompleta`: o agente registrou o início da
 * negociação e nunca registrou um desfecho, então o suplicante parou de
 * responder no meio. A coluna Fase diz exatamente onde parou — que é o que
 * antes só o tcpdump respondia.
 */

const RESULTADO_META: Record<RadiusAuthResultadoDTO, { label: string; cor: string }> = {
    ok: { label: 'Autenticou', cor: 'var(--status-online)' },
    falha: { label: 'Rejeitado', cor: 'var(--status-offline)' },
    incompleta: { label: 'Incompleta', cor: 'var(--status-warn)' },
};

const MOTIVO_LABEL: Record<string, string> = {
    usuario_inexistente: 'Usuário não existe',
    senha_incorreta: 'Senha incorreta',
    usuario_inativo: 'Usuário inativo',
    grupo_inativo: 'Grupo inativo',
    servico_nao_permitido: 'Serviço não permitido para o grupo',
    fora_do_horario: 'Fora do horário permitido',
    backend_indisponivel: 'Backend indisponível',
    metodo_nao_suportado: 'Cliente não suporta o método EAP oferecido',
    sessao_expirada: 'Sessão expirou antes de concluir',
    erro_protocolo: 'Erro de protocolo',
    erro_tls: 'Falha no handshake TLS',
    cliente_recusou_ca: 'Cliente recusou CA',
    sem_resposta_cliente: 'Cliente parou de responder',
    credencial_pendente: 'Credencial não estava em cache a tempo',
};

/**
 * Explica um desfecho de falha cuja causa não está no nosso lado. Depende da fase
 * porque "o cliente parou de responder" significa coisas opostas conforme onde
 * parou: antes do TLS é o AP/perfil do dispositivo, depois do TLS o certificado já
 * foi aceito.
 */
function diagnosticoFalha(motivo: string, fase: string): string | undefined {
    switch (motivo) {
        case 'sem_resposta_cliente':
            if (fase === 'peap_start_enviado' || fase === 'identidade_recebida' || fase === 'requisicao_recebida') {
                return (
                    'Enviamos o PEAP Start e o cliente nunca respondeu — o handshake TLS sequer começou, então não é o nosso certificado. ' +
                    'Primeira suspeita: o AP descartou nossa resposta. Isso acontece quando o secret RADIUS configurado no AP/controladora ' +
                    'não é o mesmo que o agente usa (o pacote de ida entra normalmente; só a resposta é rejeitada) — confira o secret do NAS ' +
                    'acima e, se o NAS não for o roteador do appliance, o RADIUS_SECRET do agente. Descartado isso, é o perfil 802.1X do ' +
                    'dispositivo: no Windows, modo "computador" fora de domínio (evento 12013 do WLAN-AutoConfig) — use "Autenticação de ' +
                    'usuário" + "Salvar credenciais"; no Android, CA = "não validar" e Fase 2 = MSCHAPV2.'
                );
            }
            if (fase === 'tls_handshake') {
                return 'O cliente começou o handshake TLS e parou no meio. Verifique se o suplicante aceita nosso certificado autoassinado.';
            }
            return (
                'O túnel TLS foi estabelecido (o certificado foi aceito) e o cliente ou o AP pararam de responder na autenticação interna. ' +
                'Se o detalhe traz "veredito_nao_entregue", o resultado ficou pronto depois que o AP desistiu de esperar — veja o motivo e a duração.'
            );
        case 'erro_tls':
            return 'O cliente falou TLS e o handshake falhou. O erro abaixo vem direto da pilha TLS e diz o quê: recusa do certificado, versão ou cifra sem interseção.';
        case 'cliente_recusou_ca':
            return (
                'O dispositivo recebeu nosso certificado e o recusou porque não confia nele; a senha nem chegou a ser enviada. ' +
                'Corrija no perfil Wi-Fi do dispositivo: baixe o certificado em "Certificado Wi-Fi", nesta tela de RADIUS, instale-o e ' +
                'selecione-o como CA. Se vários dispositivos começaram a falhar juntos, o certificado do agente pode ter mudado.'
            );
        case 'credencial_pendente':
            return (
                'O usuário não estava no cache local do agente e a atualização vinda do backend não chegou dentro do tempo que o AP espera por ' +
                'uma resposta. A atualização continua em background: uma nova tentativa normalmente autentica. Se repetir, verifique o link ' +
                'agente ↔ backend e se o usuário está num grupo que o NAS consegue resolver (grupo padrão, ou informe "grupo\\usuário").'
            );
        default:
            return undefined;
    }
}

/** Alertas fatais com que o suplicante recusa o certificado, no texto do `crypto/tls`. */
const ALERTA_CERTIFICADO_RECUSADO = /remote error: tls: (unknown certificate authority|bad certificate|unknown certificate)$/;

/**
 * O abandono e a falha de TLS terminam no mesmo `Handshake()` do agente, e até a
 * v0.17.2 os dois eram gravados como `erro_tls`. A evidência para separá-los já está
 * na linha: um handshake que morre em `EOF` (ou `closed pipe`) é o reaper fechando o
 * pipe depois de 30s de silêncio — a pilha TLS nunca recebeu um byte, então não há o
 * que ela pudesse recusar. Reconstruímos aqui para que a tela não minta enquanto
 * houver agente antigo em campo.
 *
 * O alerta TLS do cliente é a mesma evidência: não existe motivo próprio no agente, e
 * a falha de handshake pode chegar gravada como `erro_protocolo` (o `serveFromTunnel`
 * sobrescreve o motivo quando o Result traz erro).
 */
export function motivoEfetivo(t: RadiusAuthTentativaDTO): string | null | undefined {
    const erro = typeof t.detalhe?.erro === 'string' ? t.detalhe.erro : '';
    if (t.motivo === 'erro_tls' || t.motivo === 'erro_protocolo') {
        if (ALERTA_CERTIFICADO_RECUSADO.test(erro)) return 'cliente_recusou_ca';
    }
    if (t.motivo !== 'erro_tls') return t.motivo;
    return /^EOF$|closed pipe/i.test(erro) ? 'sem_resposta_cliente' : t.motivo;
}

const FASE_LABEL: Record<string, string> = {
    requisicao_recebida: 'Requisição recebida',
    identidade_recebida: 'Identidade recebida',
    peap_start_enviado: 'PEAP Start enviado',
    tls_handshake: 'Handshake TLS',
    tls_estabelecido: 'TLS estabelecido',
    mschapv2_interno: 'MS-CHAPv2 interno',
    concluida: 'Concluída',
};

/** Explica o que a ausência de desfecho significa naquela fase específica. */
const DIAGNOSTICO_INCOMPLETA: Record<string, string> = {
    peap_start_enviado:
        'O AP entregou nosso PEAP Start e o cliente não respondeu. Normalmente é o perfil Wi-Fi do dispositivo: sem certificado CA definido, o Android aborta antes de iniciar o TLS.',
    tls_handshake:
        'O cliente começou o handshake TLS e parou no meio. Verifique se o suplicante aceita nosso certificado autoassinado.',
    tls_estabelecido:
        'O túnel TLS foi estabelecido (o certificado foi aceito) mas a autenticação interna não concluiu.',
    mschapv2_interno:
        'A troca MS-CHAPv2 começou e não terminou. É o ponto em que o agente consulta a credencial; se a linha ficar assim por mais de 30 s, o agente ainda não reportou o desfecho — atualize.',
};

const NAS_PORT_TYPE_LABEL: Record<number, string> = {
    5: 'VPN/PPP',
    15: 'Hotspot',
    19: 'Wi-Fi',
};

function formatarData(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR');
}

function DetalheTentativa({ t }: { t: RadiusAuthTentativaDTO }) {
    const motivo = motivoEfetivo(t);
    const diagnostico =
        t.resultado === 'incompleta'
            ? DIAGNOSTICO_INCOMPLETA[t.fase]
            : motivo
              ? diagnosticoFalha(motivo, t.fase)
              : undefined;
    const linhas: Array<[string, React.ReactNode]> = [
        ['Sessão', <span style={{ userSelect: 'all', fontFamily: 'monospace' }}>{t.sessao_id}</span>],
        ['NAS', t.nas_ip ?? '—'],
        ['Identificador do NAS', t.nas_identifier ?? '—'],
        ['AP / SSID', t.called_station_id ?? '—'],
        ['MAC do cliente', t.calling_station_id ?? '—'],
        ['Tipo de porta', t.nas_port_type != null ? (NAS_PORT_TYPE_LABEL[t.nas_port_type] ?? String(t.nas_port_type)) : '—'],
        // "Duração" é o tempo do agente (soma das rodadas do handler) — o que o
        // sistema levou. O relógio de parede inclui o usuário decidindo confiar
        // no certificado e o AP retransmitindo; fica como contexto, não como
        // medida. Linhas antigas (sem processamento_ms) mostram só o total.
        ['Duração', t.processamento_ms != null ? `${t.processamento_ms} ms` : '—'],
        [
            'Tempo total',
            t.duracao_ms != null
                ? `${t.duracao_ms} ms${t.processamento_ms != null && t.duracao_ms - t.processamento_ms > 1000 ? ' (inclui espera pelo cliente/AP)' : ''}`
                : '—',
        ],
    ];

    return (
        <VBox style={{ gap: '8px', padding: '12px 16px', background: 'var(--tc-color-surface-2, rgba(0,0,0,0.03))' }}>
            {diagnostico && (
                <span style={{ color: 'var(--status-warn)' }}>{diagnostico}</span>
            )}
            {linhas.map(([rotulo, valor]) => (
                <HBox key={rotulo} style={{ gap: '8px' }}>
                    <span style={{ minWidth: '180px', color: 'var(--tc-color-text-muted, #999)' }}>{rotulo}</span>
                    <span>{valor}</span>
                </HBox>
            ))}
            {t.detalhe && Object.keys(t.detalhe).length > 0 && (
                <pre style={{ margin: 0, fontSize: '12px', overflowX: 'auto' }}>
                    {JSON.stringify(t.detalhe, null, 2)}
                </pre>
            )}
        </VBox>
    );
}

export default function TentativasModal({ show, onClose }: { show: boolean; onClose: () => void }) {
    const [filtroResultado, setFiltroResultado] = React.useState<'' | RadiusAuthResultadoDTO>('');
    const [filtroUsuario, setFiltroUsuario] = React.useState('');
    const [expandida, setExpandida] = React.useState<number | null>(null);

    const query = useQuery(
        ['radius-tentativas', filtroResultado, filtroUsuario],
        async () => {
            const params = new URLSearchParams();
            if (filtroResultado) params.set('resultado', filtroResultado);
            if (filtroUsuario) params.set('username', filtroUsuario);
            params.set('limit', '100');
            const res = await axios.get<{ ok: boolean; result: RadiusAuthTentativaListDTO }>(
                `/api/radius/tentativas?${params.toString()}`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        // Diagnóstico é acompanhado ao vivo: o operador costuma estar com o
        // cliente ao telefone tentando conectar enquanto olha esta tela.
        { refetchInterval: show ? 5_000 : false, enabled: show },
    );

    const cabecalhos: DataTableHeader[] = [
        {
            label: '',
            width: '32px',
            render: (t) => {
                const row = t as RadiusAuthTentativaDTO;
                return (
                    <span
                        role="button"
                        title="Ver detalhes"
                        style={{ cursor: 'pointer', display: 'inline-flex' }}
                        onClick={() => setExpandida((atual) => (atual === row.id ? null : row.id))}
                    >
                        <Icon path={expandida === row.id ? mdiChevronDown : mdiChevronRight} size={0.8} />
                    </span>
                );
            },
        },
        { label: 'Quando', render: (t) => formatarData((t as RadiusAuthTentativaDTO).data_inicio) },
        {
            label: 'Usuário',
            render: (t) => <span style={{ userSelect: 'all' }}>{(t as RadiusAuthTentativaDTO).username || '—'}</span>,
        },
        {
            label: 'Resultado',
            render: (t) => {
                const meta = RESULTADO_META[(t as RadiusAuthTentativaDTO).resultado];
                return <span style={{ color: meta.cor, fontWeight: 600 }}>{meta.label}</span>;
            },
        },
        {
            label: 'Motivo',
            render: (t) => {
                const row = t as RadiusAuthTentativaDTO;
                if (row.resultado === 'incompleta') {
                    return <span>Parou em: {FASE_LABEL[row.fase] ?? row.fase}</span>;
                }
                const motivo = motivoEfetivo(row);
                if (!motivo) return '—';
                return MOTIVO_LABEL[motivo] ?? motivo;
            },
        },
        { label: 'Método', render: (t) => (t as RadiusAuthTentativaDTO).metodo.toUpperCase() },
        {
            label: 'AP / SSID',
            render: (t) => (t as RadiusAuthTentativaDTO).called_station_id ?? '—',
        },
    ];

    const itens = query.data?.itens ?? [];

    return (
        <Modal show={show} onClose={onClose} title="Tentativas de autenticação" size="lg" hideControls>
            <VBox style={{ gap: '12px' }}>
                <Descricao>
                    Últimas 24 horas. Uma tentativa <strong>incompleta</strong> é aquela em que o
                    cliente parou de responder no meio da negociação — a coluna Motivo mostra até
                    onde ela chegou.
                </Descricao>

                <HBox style={{ gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <CampoSeletor
                        label="Resultado"
                        value={filtroResultado}
                        onChange={(v) => setFiltroResultado((v ?? '') as '' | RadiusAuthResultadoDTO)}
                        options={[
                            { id: '', label: 'Todos' },
                            { id: 'ok', label: 'Autenticou' },
                            { id: 'falha', label: 'Rejeitado' },
                            { id: 'incompleta', label: 'Incompleta' },
                        ]}
                    />
                    <CampoTexto
                        label="Usuário"
                        value={filtroUsuario}
                        onChange={(v) => setFiltroUsuario(v)}
                        placeholder="filtrar por nome"
                    />
                    <Botao
                        variant="outline-secondary"
                        icon={mdiRefresh}
                        onClick={() => query.refetch()}
                        disabled={query.isFetching}
                    >
                        Atualizar
                    </Botao>
                </HBox>

                <DataTable
                    cabecalhos={cabecalhos}
                    linhas={itens}
                    isLoading={query.isLoading}
                    rowStyle={(t) =>
                        (t as RadiusAuthTentativaDTO).id === expandida
                            ? { background: 'var(--tc-color-surface-2, rgba(0,0,0,0.04))' }
                            : {}
                    }
                    renderSubRow={(t) =>
                        (t as RadiusAuthTentativaDTO).id === expandida ? (
                            <DetalheTentativa t={t as RadiusAuthTentativaDTO} />
                        ) : null
                    }
                />

                {!query.isLoading && itens.length === 0 && (
                    <Descricao>Nenhuma tentativa registrada nas últimas 24 horas.</Descricao>
                )}
            </VBox>
        </Modal>
    );
}
